import { useState, useRef } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { ImageCropper } from '@/components/ImageCropper';

/**
 * Comic image data from the backend.
 */
interface ComicImage {
    id: number;
    url: string;
    alt_text: string | null;
    sort_order: number;
}

/**
 * Comic data from the backend.
 */
interface Comic {
    id: number;
    title: string | null;
    slug: string;
    artist_name: string;
    artist_url: string | null;
    thumbnail_url: string | null;
    is_published: boolean;
    published_at: string | null;
    created_at: string;
    images: ComicImage[];
}

/**
 * Props for the Edit page.
 */
interface Props {
    comic: Comic;
}

/**
 * GM Comics Edit page - form to edit an existing newspaper comic.
 */
export default function Edit({ comic }: Props) {
    // Form fields
    const [title, setTitle] = useState(comic.title || '');
    const [artistName, setArtistName] = useState(comic.artist_name);
    const [artistUrl, setArtistUrl] = useState(comic.artist_url || '');
    const [thumbnail, setThumbnail] = useState<File | null>(null);
    const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(comic.thumbnail_url);
    const [existingImages, setExistingImages] = useState<ComicImage[]>(comic.images);
    const [existingAltTexts, setExistingAltTexts] = useState<Record<number, string>>(
        Object.fromEntries(comic.images.map((img) => [img.id, img.alt_text || '']))
    );
    const [newImages, setNewImages] = useState<File[]>([]);
    const [newImagePreviews, setNewImagePreviews] = useState<string[]>([]);
    const [newAltTexts, setNewAltTexts] = useState<string[]>([]);
    const [imagesToRemove, setImagesToRemove] = useState<number[]>([]);
    const [isPublished, setIsPublished] = useState(comic.is_published);

    // Image cropper state
    const [imageToCrop, setImageToCrop] = useState<string | null>(null);
    const thumbnailInputRef = useRef<HTMLInputElement>(null);
    const imagesInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Handle thumbnail file selection - opens the cropper modal.
     */
    const handleThumbnailSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            setImageToCrop(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    /**
     * Handle crop completion - receives the cropped image blob.
     */
    const handleCropComplete = (croppedBlob: Blob) => {
        const croppedFile = new File([croppedBlob], 'thumbnail.jpg', { type: 'image/jpeg' });
        setThumbnail(croppedFile);
        setThumbnailPreview(URL.createObjectURL(croppedBlob));
        setImageToCrop(null);
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.value = '';
        }
    };

    /**
     * Handle crop cancellation.
     */
    const handleCropCancel = () => {
        setImageToCrop(null);
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.value = '';
        }
    };

    /**
     * Handle new comic images selection.
     */
    const handleImagesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setNewImages((prev) => [...prev, ...files]);
        setNewAltTexts((prev) => [...prev, ...files.map(() => '')]);

        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = () => {
                setNewImagePreviews((prev) => [...prev, reader.result as string]);
            };
            reader.readAsDataURL(file);
        });

        if (imagesInputRef.current) {
            imagesInputRef.current.value = '';
        }
    };

    /**
     * Mark an existing image for removal.
     */
    const handleRemoveExistingImage = (imageId: number) => {
        setImagesToRemove((prev) => [...prev, imageId]);
        setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
    };

    /**
     * Remove a new image from the list.
     */
    const handleRemoveNewImage = (index: number) => {
        setNewImages((prev) => prev.filter((_, i) => i !== index));
        setNewImagePreviews((prev) => prev.filter((_, i) => i !== index));
        setNewAltTexts((prev) => prev.filter((_, i) => i !== index));
    };

    /**
     * Update alt text for an existing image.
     */
    const handleExistingAltTextChange = (imageId: number, value: string) => {
        setExistingAltTexts((prev) => ({ ...prev, [imageId]: value }));
    };

    /**
     * Update alt text for a new image.
     */
    const handleNewAltTextChange = (index: number, value: string) => {
        setNewAltTexts((prev) => {
            const newAltTexts = [...prev];
            newAltTexts[index] = value;
            return newAltTexts;
        });
    };

    /**
     * Handle form submission.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        const formData = new FormData();
        formData.append('_method', 'PATCH');
        if (title) formData.append('title', title);
        formData.append('artist_name', artistName);
        if (artistUrl) formData.append('artist_url', artistUrl);
        if (thumbnail) formData.append('thumbnail', thumbnail);
        newImages.forEach((image) => {
            formData.append('new_images[]', image);
        });
        newAltTexts.forEach((altText) => {
            formData.append('new_alt_texts[]', altText);
        });
        Object.entries(existingAltTexts).forEach(([imageId, altText]) => {
            formData.append(`existing_alt_texts[${imageId}]`, altText);
        });
        imagesToRemove.forEach((id) => {
            formData.append('remove_images[]', id.toString());
        });
        formData.append('is_published', isPublished ? '1' : '0');

        router.post(`/gm/newspaper/comics/${comic.id}`, formData, {
            forceFormData: true,
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => setIsSubmitting(false),
        });
    };

    // Calculate total remaining images
    const totalImages = existingImages.length + newImages.length;

    return (
        <GmLayout title="Edit Comic" activePath="/gm/newspaper">
            <div className="max-w-4xl">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-white">🎨 Edit Comic</h1>
                        <p className="text-sm text-gray-400">
                            {comic.is_published ? 'Published' : 'Draft'} • {comic.images.length} image{comic.images.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <Link href="/gm/newspaper/comics" className="text-gray-400 hover:text-white transition-colors">
                        ← Back to Features
                    </Link>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Info Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Comic Information</h2>

                        {/* Title (optional) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Title (optional)
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="Leave blank for untitled comic"
                            />
                            {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
                        </div>

                        {/* Artist Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Artist Name <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                value={artistName}
                                onChange={(e) => setArtistName(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="Artist's name or handle"
                                required
                            />
                            {errors.artist_name && <p className="mt-1 text-sm text-red-400">{errors.artist_name}</p>}
                        </div>

                        {/* Artist URL */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Artist URL (optional)
                            </label>
                            <input
                                type="url"
                                value={artistUrl}
                                onChange={(e) => setArtistUrl(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="https://artist-website.com"
                            />
                            {errors.artist_url && <p className="mt-1 text-sm text-red-400">{errors.artist_url}</p>}
                        </div>

                        {/* Thumbnail */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Thumbnail
                            </label>
                            <p className="text-xs text-gray-500 mb-2">
                                Square image (1:1 aspect ratio) for the Features grid
                            </p>
                            {thumbnailPreview && (
                                <div className="mb-3">
                                    <img
                                        src={thumbnailPreview}
                                        alt="Thumbnail preview"
                                        className="w-32 h-32 object-cover rounded border border-slate-600"
                                    />
                                </div>
                            )}
                            <input
                                ref={thumbnailInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailSelect}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer"
                            />
                            <p className="text-xs text-gray-500 mt-1">Upload a new image to replace the current thumbnail</p>
                            {errors.thumbnail && <p className="mt-1 text-sm text-red-400">{errors.thumbnail}</p>}
                        </div>
                    </div>

                    {/* Comic Images Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Comic Images</h2>

                        {/* Existing Images */}
                        {existingImages.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-300 mb-2">Current Images</h3>
                                <div className="space-y-4 mb-4">
                                    {existingImages.map((image, index) => (
                                        <div key={image.id} className="bg-slate-700 rounded overflow-hidden p-3">
                                            <div className="flex gap-4">
                                                <div className="relative flex-shrink-0 w-32">
                                                    <img
                                                        src={image.url}
                                                        alt={`Image ${index + 1}`}
                                                        className="w-32 h-32 object-cover rounded"
                                                    />
                                                    <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                        {index + 1}
                                                    </div>
                                                </div>
                                                <div className="flex-1 space-y-2">
                                                    <div>
                                                        <label className="block text-xs text-gray-400 mb-1">
                                                            Alt Text (Image Description)
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={existingAltTexts[image.id] || ''}
                                                            onChange={(e) => handleExistingAltTextChange(image.id, e.target.value)}
                                                            placeholder="Describe this image for accessibility"
                                                            className="w-full px-2 py-1 text-sm bg-slate-600 border border-slate-500 rounded text-white placeholder-gray-400"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveExistingImage(image.id)}
                                                        className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                                                    >
                                                        ✕ Remove
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* New Images */}
                        {newImagePreviews.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-300 mb-2">New Images (to be added)</h3>
                                <div className="space-y-4 mb-4">
                                    {newImagePreviews.map((preview, index) => (
                                        <div key={index} className="bg-slate-700 rounded overflow-hidden p-3 border-2 border-green-500">
                                            <div className="flex gap-4">
                                                <div className="relative flex-shrink-0 w-32">
                                                    <img
                                                        src={preview}
                                                        alt={`New image ${index + 1}`}
                                                        className="w-32 h-32 object-cover rounded"
                                                    />
                                                    <div className="absolute top-1 left-1 bg-green-600 text-white text-xs px-2 py-1 rounded">
                                                        New
                                                    </div>
                                                </div>
                                                <div className="flex-1 space-y-2">
                                                    <div>
                                                        <label className="block text-xs text-gray-400 mb-1">
                                                            Alt Text (Image Description)
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={newAltTexts[index] || ''}
                                                            onChange={(e) => handleNewAltTextChange(index, e.target.value)}
                                                            placeholder="Describe this image for accessibility"
                                                            className="w-full px-2 py-1 text-sm bg-slate-600 border border-slate-500 rounded text-white placeholder-gray-400"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveNewImage(index)}
                                                        className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                                                    >
                                                        ✕ Remove
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <input
                            ref={imagesInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImagesSelect}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer"
                        />
                        <p className="text-xs text-gray-500">Add more images to this comic</p>
                        {errors.new_images && <p className="mt-1 text-sm text-red-400">{errors.new_images}</p>}
                    </div>

                    {/* Publish Status */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <div className="flex items-center">
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={isPublished}
                                    onChange={(e) => setIsPublished(e.target.checked)}
                                    className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                />
                                Published (visible to all users)
                            </label>
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting || totalImages === 0}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                        <Link
                            href="/gm/newspaper/comics"
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>

            {/* Image Cropper Modal for Thumbnail */}
            {imageToCrop && (
                <ImageCropper
                    imageSrc={imageToCrop}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                    targetWidth={400}
                    targetHeight={400}
                />
            )}
        </GmLayout>
    );
}

